import type { BidMetricsByTime, ServerSideMetricsDetailByTime } from '@pubstack/common/src/analytics/query'
import type { CurrencySymbol } from '@pubstack/common/src/currency'
import type { FunctionComponent } from 'react'
import { Chart, type ChartWrapperOptions } from 'react-google-charts'
import { DataColors } from '~/assets/style/colors'
import { type ChartTooltipMetricProps, generateChartTooltip } from '~/components/ChartTooltip'
import { Icon } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'
import { Widget, type WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions, ChartContainer, ChartWrapperSmall } from '~/modules/analytics/AnalyticsCharts'
import { bidRequests, bidResponses, hbImpressionCount, serverSideBidRate, serverSideBidRequests, serverSideBidResponses, serverSideImpressionCount } from '~/modules/analytics/common/formulas'
import { asPercentage, type Formula } from '~/modules/analytics/common/formulas/operation'
import type { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

export type ServerSideFunnelChartMetricTooltip = {
  name: string
  displayable: (data: BidMetricsByTime) => string
}

type ServerSideFunnelMetric = {
  name: string
  formula: Formula<any>
  compute?: (data: ServerSideMetricsDetailByTime, index: number) => number
  sum?: (data: ServerSideMetricsDetailByTime) => number
  displayable?: (value: number) => string
  tooltip?: string
  annotation: string | undefined
}
const TooltipContent = () => (
  <>
    <div>
      {bidRequests.name}: {ANALYTICS_TOOLTIPS.BIDDER_DETAILS_BID_REQUESTS}
    </div>
    <br />
    <div>
      {bidResponses.name}: {ANALYTICS_TOOLTIPS.BIDDER_DETAILS_BID_RESPONSES}
    </div>
    {/*<br />*/}
    {/*<div>*/}
    {/*  {hbBidWinCount.name}: {ANALYTICS_TOOLTIPS.BIDDER_DETAILS_PREBID_WIN_BIDS}*/}
    {/*</div>*/}
    <br />
    <div>
      {hbImpressionCount.name}: {ANALYTICS_TOOLTIPS.BIDDER_DETAILS_PREBID_IMPRESSIONS}
    </div>
  </>
)

const getTooltip = (metric: ServerSideFunnelMetric, data: ServerSideMetricsDetailByTime) => {
  let title = ''
  let metrics: ServerSideFunnelChartMetricTooltip[] = []

  const bidResponsesSum = serverSideBidResponses.isComputable(data) ? serverSideBidResponses.sum(data) : 0
  // const bidWinCountSum = hbBidWinCount.isComputable(data) ? hbBidWinCount.sum(data) : 0
  const impressionCountSum = serverSideImpressionCount.isComputable(data) ? serverSideImpressionCount.sum(data) : 0
  // const bidResponseCpmSumSum = bidResponseCpmSum.isComputable(data) ? bidResponseCpmSum.sum(data) : 0
  if (metric.name === 'bidRequests') {
    title = bidRequests.name
    metrics = [
      {
        name: 'Count',
        displayable: () => (serverSideBidRequests.isComputable(data) ? serverSideBidRequests.displayable(serverSideBidRequests.sum(data)) : 'NA'),
      },
    ]
  }
  if (metric.name === 'bidResponses') {
    title = bidResponses.name
    metrics = [
      {
        name: 'Count',
        displayable: () => `${serverSideBidResponses.displayable(bidResponsesSum)} (${serverSideBidRate.isComputable(data) ? serverSideBidRate.displayable(serverSideBidRate.sum(data)) : 'NA'})`,
      },
    ]
    // } else if (metric.name === 'hbBidWinCount') {
    //   title = hbBidWinCount.name
    //   metrics = [
    //     {
    //       name: 'Count',
    //       displayable: () => `${hbBidWinCount.displayable(bidWinCountSum)} (${bidRequests.isComputable(data) ? asPercentage(bidRequests.percentage(bidWinCountSum, data) * 100) : 'NA'})`,
    //     },
    //     {
    //       name: 'Amount',
    //       displayable: () =>
    //         `${hbWinningCpmSum.displayable(hbWinningCpmSum.isComputable(data) ? hbWinningCpmSum.sum(data) : 0, currencySymbol)} (${(
    //           (headerBiddingWinningCpmSumSum / bidResponseCpmSumSum) * 100
    //         ).toPrecision(3)}%)`,
    //     },
    //     {
    //       name: bidWinCpm.name,
    //       displayable: () => bidWinCpm.displayable(bidWinCpm.isComputable(data) ? bidWinCpm.sum(data) : 0, currencySymbol),
    //     },
    //     {
    //       name: hbWinRate.name,
    //       displayable: () => hbWinRate.displayable(hbWinRate.isComputable(data) ? hbWinRate.sum(data) : 0),
    //     },
    //   ]
  }
  if (metric.name === 'hbImpressionCount') {
    title = hbImpressionCount.name
    metrics = [
      {
        name: 'Count',
        displayable: () =>
          `${serverSideImpressionCount.displayable(impressionCountSum)} (${serverSideBidRequests.isComputable(data) ? asPercentage(serverSideBidRequests.percentage(impressionCountSum, data) * 100) : 'NA'})`,
      },
    ]
  }
  const metricsLines: ChartTooltipMetricProps[] = metrics.map((metric) => {
    const value = metric.displayable(data)
    return {
      label: metric.name,
      value,
      iconName: 'empty',
    }
  })
  return generateChartTooltip({ date: title, metrics: [metricsLines] })
}

const convertData = (data: ServerSideMetricsDetailByTime, metrics: ServerSideFunnelMetric[], _currencySymbol: CurrencySymbol) => {
  if (!data.epoch.length) {
    return []
  }
  return metrics.map((metric) => [metric.formula.name, getTooltip(metric, data), metric.formula.isComputable(data) ? metric.formula.sum(data) : 0, metric.annotation])
}

type BuildedData = {
  chartData: any[][]
  chartOptions: ChartWrapperOptions['options']
}
const buildData = ({ data, currencySymbol }: { data: ServerSideMetricsDetailByTime; currencySymbol: CurrencySymbol }): BuildedData => {
  const metrics: ServerSideFunnelMetric[] = data.epoch.length
    ? [
        {
          name: 'bidRequests',
          formula: serverSideBidRequests,
          annotation: undefined,
        },
        {
          name: 'bidResponses',
          formula: serverSideBidResponses,
          annotation: serverSideBidRate.isComputable(data) ? `${serverSideBidRate.name} ${serverSideBidRate.displayable(serverSideBidRate.sum(data))}` : undefined,
        },
        // {
        //   name: 'hbBidWinCount',
        //   formula: serverSideBidWinCount,
        //   annotation: `${serverSideWinRate.name} ${serverSideWinRate.displayable(serverSideWinRate.isComputable(data) ? serverSideWinRate.sum(data) : 0)}`,
        // },
        {
          name: 'hbImpressionCount',
          formula: serverSideImpressionCount,
          annotation: undefined,
          // annotation: `${serverSideImpressionRate.name} ${serverSideImpressionRate.displayable(serverSideImpressionRate.isComputable(data) ? serverSideImpressionRate.sum(data) : 0)}`,
        },
      ]
    : []
  const chartOptions: ChartWrapperOptions['options'] = {
    ...AnalyticsDefaultChartOptions,
    bar: { groupWidth: '100%' },
    seriesType: 'bars',
    crosshair: { ...AnalyticsDefaultChartOptions.crosshair, trigger: 'both' },
    vAxis: {
      ...AnalyticsDefaultChartOptions.vAxis,
      gridlines: { count: -1 },
      format: 'short',
      minValue: 0,
    },
    annotations: {
      style: 'point',
      alwaysOutside: true,
    },
    colors: [DataColors.Ming],
    isStacked: false,
  }
  const chartData = [
    [
      'Label',
      { type: 'string', role: 'tooltip', p: { html: true } },
      'value',
      {
        type: 'string',
        role: 'annotation',
      },
    ],
    ...convertData(data, metrics, currencySymbol),
  ]

  return {
    chartData,
    chartOptions,
  }
}

type ServerSideWidgetFunnelProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel'> & {
    data: ServerSideMetricsDetailByTime
    currencySymbol: CurrencySymbol
  }
export const ServerSideWidgetFunnel: FunctionComponent<ServerSideWidgetFunnelProps> = ({ data, currencySymbol, ...props }) => {
  const { chartData, chartOptions } = buildData({ data, currencySymbol })
  return (
    <Widget
      {...props}
      icon={'bidder'}
      title={'Bidder funnel'}
      info={
        <Tooltip title={<TooltipContent />} positions={['left']} align={'start'}>
          <Icon name={'help_outline'} />
        </Tooltip>
      }
      refreshTrackingLabel={'ana-bidders-detail-funnel-refresh'}
    >
      <ChartContainer>
        <ChartWrapperSmall>
          {!!data.epoch.length && <Chart chartType={'ColumnChart'} width={'100%'} height={'100%'} options={chartOptions} data={chartData} chartVersion={'current'} />}
        </ChartWrapperSmall>
      </ChartContainer>
    </Widget>
  )
}
